import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import { Eye, RotateCcw, Trash } from "lucide-react";
import LoadingScreen from "../../../utils/LoadingScreen";
import NoIndex from "../../../components/NoIndex";

const TemplateStatus = () => {
  const navigate = useNavigate();

  const { templates, loading, fetchVendorTemplates, deleteVendorTemplate } =
    useTemplates();

  const { whatsappNumbers } = useWhatsappNumbers();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!selectedNumber) return;

    setRefreshing(true);

    const startTime = Date.now();

    await fetchVendorTemplates(selectedNumber);

    const elapsed = Date.now() - startTime;
    const remainingTime = 5000 - elapsed;

    if (remainingTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }

    setRefreshing(false);
  };

  useEffect(() => {
    if (selectedNumber) {
      fetchVendorTemplates(selectedNumber);
    }
  }, [selectedNumber]);

  useEffect(() => {
    if (whatsappNumbers?.length > 0) {
      setSelectedNumber(whatsappNumbers[0].phone_no_id);
    }
  }, [whatsappNumbers]);

  const handleDelete = async (id, name) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete template "${name}"?`,
    );

    if (!confirmDelete) return;

    const res = await deleteVendorTemplate(id, selectedNumber);

    if (res?.responseCode === "00") {
      toast.success("Template deleted successfully");
    } else {
      toast.error("Failed to delete template");
    }
  };

  const filteredTemplates = templates?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  console.log("Selected Number:", selectedNumber);

  return (
    <>
      <NoIndex />
      <ToastContainer />
      <div className="p-8 min-h-full montserrat-font-1">
        {/* Top Card */}
        <div className="bg-white rounded-xl shadow p-6 mb-8 border montserrat-font-1">
          <div className="flex justify-between items-center mb-6 montserrat-font-1">
            <h2 className="text-xl font-semibold">Template Status</h2>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/WhatsApp/Create-Template")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2 rounded-md font-medium"
              >
                + Create New Template
              </button>

              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 border rounded-md px-3 py-2 bg-blue-500 text-white"
              >
                <RotateCcw strokeWidth={2.5} size={16} />
                Refresh
              </button>
            </div>
          </div>

          <hr className="mb-6" />

          {/* Filters */}
          <div className="flex gap-6 montserrat-font-1">
            <div>
              <label className="block text-sm font-medium mb-1">
                WhatsApp Number
              </label>
              <select
                value={selectedNumber}
                onChange={(e) => setSelectedNumber(e.target.value)}
                className="border rounded-md px-4 py-2 w-72"
              >
                {whatsappNumbers?.map((num) => (
                  <option
                    key={`${num.phone_no_id}-${num.phone_no}`}
                    value={num.phone_no_id}
                  >
                    {num.phone_no_id} - ({num.phone_no})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Search Template
              </label>
              <input
                type="text"
                placeholder="Search Template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border rounded-md px-4 py-2 w-72"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow overflow-hidden border montserrat-font-1">
          <table className="min-w-full text-sm text-center border border-gray-300 montserrat-font-1">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="py-4 border border-gray-300">Template ID</th>
                <th className="py-4 border border-gray-300">Template Name</th>
                <th className="py-4 border border-gray-300">Language</th>
                <th className="py-4 border border-gray-300">Category</th>
                <th className="py-4 border border-gray-300">Status</th>
                <th className="py-4 border border-gray-300">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10">
                    <div className="flex justify-center items-center">
                      <LoadingScreen text="Updating ..." />
                    </div>
                  </td>
                </tr>
              ) : filteredTemplates?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-gray-400 text-center">
                    No templates found
                  </td>
                </tr>
              ) : (
                filteredTemplates?.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    <td className="border border-gray-300 py-4">{item.id}</td>
                    <td className="border border-gray-300 py-4">{item.name}</td>
                    <td className="border border-gray-300 py-4">
                      {item.language}
                    </td>
                    <td className="border border-gray-300 py-4">
                      {item.category}
                    </td>
                    <td className="border border-gray-300 py-4">
                      {item.status === "APPROVED" ? (
                        <span className="bg-green-100 text-green-600 px-4 py-1 rounded-full text-xs font-semibold">
                          Approved
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-600 px-4 py-1 rounded-full text-xs font-semibold">
                          {item.status}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex justify-center gap-4" montserrat-font-1>
                        {/* View Button */}
                        <button
                          onClick={() =>
                            navigate(
                              `/WhatsApp/TemplateView?templateID=${item.id}&templateName=${item.name}`,
                            )
                          }
                          className="text-gray-500 hover:text-black transition"
                        >
                          <Eye size={22} />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(item.id, item.name)}
                          className="bg-red-100 text-red-500 p-2 rounded-md hover:bg-red-200 transition"
                        >
                          <Trash size={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {filteredTemplates?.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-8 text-gray-400">
                    No templates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (UI Only Example) */}
        <div className="flex justify-center mt-6 gap-2 montserrat-font-1 items-center">
          <button className="px-3 py-1 bg-gray-200 rounded">&lt;</button>
          <button className="px-3 py-1 bg-blue-500 text-white rounded">
            1
          </button>
          <button className="px-3 py-1 bg-gray-200 rounded">&gt;</button>
        </div>
      </div>
    </>
  );
};

export default TemplateStatus;
